/**
 * exportUtils — reusable client-side CSV + PDF export for admin management
 * tables. No backend round-trip, no extra dependencies (jsPDF is already
 * installed; CSV is a plain Blob).
 *
 * Both helpers are table-shaped: pass `columns` (header label + row key) and
 * `rows` (the data already loaded in component state). Empty data is a no-op
 * so callers can bind the export button directly to the current page state
 * without guarding for empty result sets.
 *
 * Mirrors the in-browser CSV pattern used by the Strishakti superadmin
 * (Blob + temporary anchor) and the jsPDF usage in lib/certificatePdf.ts.
 */

export interface ExportColumn<T = Record<string, unknown>> {
  /** column header label */
  header: string
  /** key on each row whose value is rendered in this column */
  key: string
  /** optional formatter (e.g. dates, booleans → "Yes"/"No") */
  format?: (value: unknown, row: T) => string
}

import { jsPDF } from 'jspdf'

const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke on the next tick so the click has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

const stamp = (): string => new Date().toISOString().slice(0, 10)

/**
 * Export `rows` to a CSV file (UTF-8, RFC-4180 quoting). Returns true if a
 * file was downloaded, false if there was nothing to export.
 */
export function exportToCSV<T>(
  filename: string,
  columns: ExportColumn<T>[],
  rows: T[],
): boolean {
  if (!rows || rows.length === 0) return false

  const esc = (s: string): string => {
    // Quote any cell containing the delimiter, a quote, or a newline.
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const headerLine = columns.map((c) => esc(c.header)).join(',')
  const dataLines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = (row as Record<string, unknown>)[c.key]
        const val = c.format ? c.format(raw, row) : cellToString(raw)
        return esc(val)
      })
      .join(','),
  )

  // Prepend a BOM so Excel reads UTF-8 correctly.
  const csv = '﻿' + [headerLine, ...dataLines].join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, `${filename}-${stamp()}.csv`)
  return true
}

/**
 * Export `rows` to a simple PDF table using jsPDF directly (no html2canvas,
 * no jspdf-autotable). A4 portrait, auto-wrapping cells, header repeats on
 * each page. Returns true if a file was downloaded, false if empty.
 */
export function exportToPDF<T>(
  filename: string,
  title: string,
  columns: ExportColumn<T>[],
  rows: T[],
): boolean {
  if (!rows || rows.length === 0) return false

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 40
  const usableWidth = pageWidth - margin * 2

  // Distribute column widths proportionally; cap so one column can't dominate.
  const minWidth = 50
  const rawTotal = columns.reduce((sum, c) => sum + Math.max(c.header.length, 12), 0)
  const colWidths = columns.map((c) => {
    const w = (Math.max(c.header.length, 12) / rawTotal) * usableWidth
    return Math.max(minWidth, Math.min(w, usableWidth * 0.5))
  })
  // Normalize to exactly usableWidth.
  const scale = usableWidth / colWidths.reduce((s, w) => s + w, 0)
  const widths = colWidths.map((w) => w * scale)

  const headerFont = { size: 11, style: 'bold' as const }
  const cellFont = { size: 9, style: 'normal' as const }
  const lineH = 13
  const headerFill: [number, number, number] = [37, 99, 235] // blue-600

  let y = margin

  // Title
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(17, 24, 39)
  doc.text(title, margin, y)
  y += 8
  doc.setFontSize(9)
  doc.setTextColor(107, 114, 128)
  doc.text(`Generated: ${new Date().toLocaleString()}  •  ${rows.length} record(s)`, margin, y)
  y += 22

  const drawHeader = (topY: number): number => {
    let x = margin
    doc.setFillColor(...headerFill)
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', headerFont.style)
    doc.setFontSize(headerFont.size)
    let maxY = topY
    columns.forEach((c, i) => {
      const lines = doc.splitTextToSize(c.header, widths[i] - 6) as string[]
      doc.rect(x, topY, widths[i], lineH * lines.length, 'F')
      doc.text(lines, x + 3, topY + lineH - 3)
      x += widths[i]
      maxY = topY + lineH * lines.length
    })
    return maxY
  }

  const drawRow = (row: T, topY: number): number => {
    let x = margin
    doc.setFont('helvetica', cellFont.style)
    doc.setFontSize(cellFont.size)
    doc.setTextColor(31, 41, 55)
    // Compute wrapped lines per cell first to find the row height.
    const cellLines = columns.map((c, i) => {
      const raw = (row as Record<string, unknown>)[c.key]
      const val = c.format ? c.format(raw, row) : cellToString(raw)
      return doc.splitTextToSize(val, widths[i] - 6) as string[]
    })
    const rowH = Math.max(...cellLines.map((l) => l.length)) * lineH
    let maxY = topY + rowH
    columns.forEach((c, i) => {
      const lines = cellLines[i]
      // Subtle row separators
      doc.setDrawColor(229, 231, 235)
      doc.line(x, topY, x + widths[i], topY)
      lines.forEach((ln, li) => {
        doc.text(ln, x + 3, topY + lineH - 3 + li * lineH)
      })
      x += widths[i]
    })
    return maxY
  }

  y = drawHeader(y)
  rows.forEach((row) => {
    if (y > pageHeight - margin - lineH * 2) {
      doc.addPage()
      y = margin
      y = drawHeader(y)
    }
    y = drawRow(row, y)
  })

  // Footer line
  doc.setDrawColor(229, 231, 235)
  doc.line(margin, y, margin + usableWidth, y)

  doc.save(`${filename}-${stamp()}.pdf`)
  return true
}